/*
 * Encrypted Vuex persistence for remembered sessions.
 *
 * Persisted modules:
 * - auth
 * - navigation
 *
 * Storage:
 * - Encrypted JSON in localStorage under the default key "vuex"
 * - AES key in the "myuse" cookie
 *
 * Persistence is enabled only when the remember-me cookie is "1".
 */

import Crypto from 'crypto-js'
import Cookie from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import createPersistedState from 'vuex-persistedstate'

const ENCRYPTION_COOKIE =
  'myuse'

const REMEMBER_COOKIE =
  'remember-me'

const STORAGE_KEY =
  'vuex'

const cookieOptions = () => ({
  expires: 180,
  sameSite: 'Lax',
  path: '/',

  /*
   * Secure cookies work only over HTTPS.
   * Keep local HTTP development functional.
   */
  secure:
    typeof window !==
      'undefined' &&
    window.location.protocol ===
      'https:',
})

export default function ({
  store,
}) {
  if (
    !process.client ||
    typeof window ===
      'undefined'
  ) {
    return
  }

  /*
   * Reuse the existing key when possible.
   * Creating a new key would make existing persisted
   * state impossible to decrypt.
   */
  let encryptionToken =
    Cookie.get(
      ENCRYPTION_COOKIE
    )

  if (!encryptionToken) {
    encryptionToken =
      uuidv4()

    Cookie.set(
      ENCRYPTION_COOKIE,
      encryptionToken,
      cookieOptions()
    )
  } else {
    /*
     * Reapply the cookie with the current security
     * attributes and refresh its expiry.
     */
    Cookie.set(
      ENCRYPTION_COOKIE,
      encryptionToken,
      cookieOptions()
    )
  }

  const encryptedStorage = {
    /**
     * Retrieve and decrypt persisted Vuex JSON.
     *
     * vuex-persistedstate accepts the decrypted JSON
     * string in the same form localStorage normally returns.
     */
    getItem(key) {
      const encryptedValue =
        window.localStorage.getItem(
          key
        )

      if (!encryptedValue) {
        return null
      }

      try {
        const bytes =
          Crypto.AES.decrypt(
            encryptedValue,
            encryptionToken
          )

        const decryptedText =
          bytes.toString(
            Crypto.enc.Utf8
          )

        if (!decryptedText) {
          throw new Error(
            'Persisted state could not be decrypted'
          )
        }

        /*
         * Validate it before returning it.
         * Return the original JSON string because that
         * matches the normal Storage API contract.
         */
        JSON.parse(decryptedText)

        return decryptedText
      } catch (_) {
        /*
         * The cookie and encrypted value no longer match,
         * or the stored value is malformed.
         */
        window.localStorage.removeItem(
          key
        )

        return null
      }
    },

    /**
     * Encrypt and persist Vuex JSON.
     */
    setItem(key, value) {
      const rememberEnabled =
        Cookie.get(
          REMEMBER_COOKIE
        ) === '1'

      if (!rememberEnabled) {
        /*
         * Prevent an old remembered session from surviving
         * after Remember Me has been disabled.
         */
        window.localStorage.removeItem(
          key
        )

        return
      }

      try {
        const encryptedValue =
          Crypto.AES.encrypt(
            String(value),
            encryptionToken
          ).toString()

        window.localStorage.setItem(
          key,
          encryptedValue
        )
      } catch (_) {
        /*
         * Persistence failure must never stop the app.
         */
      }
    },

    /**
     * Remove persisted Vuex state.
     */
    removeItem(key) {
      try {
        window.localStorage.removeItem(
          key
        )
      } catch (_) {}
    },
  }

  createPersistedState({
    key: STORAGE_KEY,

    storage:
      encryptedStorage,

    /*
     * The profile is intentionally not persisted.
     * On automatic login, auth/login_user retrieves a fresh
     * profile from the backend and calls profile/recallProfile.
     */
    paths: [
      'auth',
      'navigation',
    ],
  })(store)
}